import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Calendar,
  Download,
  FolderOpen,
  Loader2,
  Play,
  Plus,
  RotateCcw,
  Save,
  Trash2,
  UserPlus,
} from 'lucide-react';
import { format } from 'date-fns';
import { useLocationContext } from '../components/Layout';
import type { ReportHistoryItem } from '../components/ReportHistory';
import { companySettingsApi, invoiceReportRunsApi, scheduleApi, shiftApi } from '../services/api';
import type { Schedule } from '../types';
import {
  addEmptyEmployeeRow,
  buildInvoiceReportFromShifts,
  createInvoiceReportPdfBlob,
  defaultMonthInputValue,
  monthYearFromInput,
  normalizeHhmmInput,
  parseInvoiceReportSnapshot,
  recalculateInvoiceTotals,
  formatInvoiceHoursThreeLine,
  getDayShifts,
  normalizeDayCell,
  type InvoiceShiftSlot,
  type InvoiceEmployeeRow,
  type InvoiceReportData,
} from '../utils/invoiceReport';
import { formatCompanyDateTime } from '../utils/companyDateFormat';

export interface InvoiceReportSavePayload {
  scheduleId: number;
  scheduleName: string;
  month: string;
  locationId?: number | null;
  recordsProcessed: number;
  grandTotalHours: number;
  parameters: Record<string, unknown>;
  snapshot: InvoiceReportData;
}

interface InvoiceReportProps {
  onSaveRun?: (payload: InvoiceReportSavePayload) => Promise<ReportHistoryItem>;
  runHistory?: ReportHistoryItem[];
  onDeleteRun?: (reportId: string) => void;
}

const InvoiceReport: React.FC<InvoiceReportProps> = ({
  onSaveRun,
  runHistory = [],
  onDeleteRun,
}) => {
  const { debouncedSelectedLocationId } = useLocationContext();
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | ''>('');
  const [monthValue, setMonthValue] = useState(defaultMonthInputValue);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [running, setRunning] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [loadBusyId, setLoadBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [reportData, setReportData] = useState<InvoiceReportData | null>(null);
  const [originalReportData, setOriginalReportData] = useState<InvoiceReportData | null>(null);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [loadedRunId, setLoadedRunId] = useState<string | null>(null);
  const schedulesRequestRef = useRef(0);

  useEffect(() => {
    const requestId = ++schedulesRequestRef.current;
    setLoadingOptions(true);
    (async () => {
      try {
        const rows = await scheduleApi.getAll();
        if (requestId !== schedulesRequestRef.current) return;
        setSchedules((rows || []).filter((s) => s.isActive).sort((a, b) => a.name.localeCompare(b.name)));
      } catch (e) {
        console.error(e);
        if (requestId === schedulesRequestRef.current) {
          setError('Could not load schedules.');
        }
      } finally {
        if (requestId === schedulesRequestRef.current) {
          setLoadingOptions(false);
        }
      }
    })();
  }, []);

  const selectedSchedule = useMemo(
    () => schedules.find((s) => s.id === selectedScheduleId) ?? null,
    [schedules, selectedScheduleId]
  );

  const sortedHistory = useMemo(
    () => [...runHistory].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [runHistory]
  );

  const applyReportData = useCallback((data: InvoiceReportData, runId: string | null = null) => {
    const final = recalculateInvoiceTotals(data);
    setReportData(final);
    setOriginalReportData(JSON.parse(JSON.stringify(final)) as InvoiceReportData);
    setLoadedRunId(runId);
    setSaveMessage(null);
  }, []);

  const handleRunReport = useCallback(async () => {
    if (!selectedScheduleId || !monthValue) {
      setError('Select a schedule and month.');
      return;
    }
    const parsed = monthYearFromInput(monthValue);
    if (!parsed) {
      setError('Invalid month.');
      return;
    }

    setRunning(true);
    setError(null);
    try {
      const monthStart = new Date(parsed.year, parsed.month - 1, 1);
      const monthEnd = new Date(parsed.year, parsed.month, 0);
      const startDate = format(monthStart, 'yyyy-MM-dd');
      const endDate = format(monthEnd, 'yyyy-MM-dd');

      const [shifts, companySettings] = await Promise.all([
        shiftApi.getAll({
          startDate,
          endDate,
          scheduleId: selectedScheduleId,
          ...(debouncedSelectedLocationId ? { locationId: debouncedSelectedLocationId } : {}),
        }),
        companySettingsApi.get(),
      ]);

      const built = buildInvoiceReportFromShifts({
        shifts,
        scheduleId: selectedScheduleId,
        scheduleName: selectedSchedule?.name ?? 'Schedule',
        companyName: companySettings?.companyName ?? 'Company',
        year: parsed.year,
        month: parsed.month,
      });

      applyReportData(built, null);
    } catch (e) {
      console.error(e);
      setError('Failed to generate report. Please try again.');
    } finally {
      setRunning(false);
    }
  }, [selectedScheduleId, monthValue, debouncedSelectedLocationId, selectedSchedule, applyReportData]);

  const updateMeta = useCallback(
    (field: 'areaName' | 'companyName' | 'monthLabel', value: string) => {
      setReportData((prev) => (prev ? { ...prev, [field]: value } : prev));
    },
    []
  );

  const updateEmployeeName = useCallback((rowId: string, name: string) => {
    setReportData((prev) => {
      if (!prev) return prev;
      const employees = prev.employees.map((r) =>
        r.id === rowId ? { ...r, employeeName: name } : r
      );
      return { ...prev, employees };
    });
  }, []);

  const updateShiftSlot = useCallback(
    (
      rowId: string,
      dayIndex: number,
      slotIndex: number,
      field: keyof InvoiceShiftSlot,
      value: string
    ) => {
      setReportData((prev) => {
        if (!prev) return prev;
        const normalized = normalizeHhmmInput(value);
        const employees = prev.employees.map((row) => {
          if (row.id !== rowId) return row;
          const days = row.days.map((cell, idx) => {
            if (idx !== dayIndex) return normalizeDayCell(cell);
            const cellNorm = normalizeDayCell(cell);
            const shifts = [...cellNorm.shifts];
            while (shifts.length <= slotIndex) shifts.push({ startTime: '', endTime: '' });
            shifts[slotIndex] = { ...shifts[slotIndex], [field]: normalized };
            return { shifts };
          });
          return { ...row, days };
        });
        return recalculateInvoiceTotals({ ...prev, employees });
      });
    },
    []
  );

  const addShiftSlot = useCallback((rowId: string, dayIndex: number) => {
    setReportData((prev) => {
      if (!prev) return prev;
      const employees = prev.employees.map((row) => {
        if (row.id !== rowId) return row;
        const days = row.days.map((cell, idx) => {
          if (idx !== dayIndex) return normalizeDayCell(cell);
          const cellNorm = normalizeDayCell(cell);
          return { shifts: [...cellNorm.shifts, { startTime: '', endTime: '' }] };
        });
        return { ...row, days };
      });
      return recalculateInvoiceTotals({ ...prev, employees });
    });
  }, []);

  const removeShiftSlot = useCallback((rowId: string, dayIndex: number, slotIndex: number) => {
    setReportData((prev) => {
      if (!prev) return prev;
      const employees = prev.employees.map((row) => {
        if (row.id !== rowId) return row;
        const days = row.days.map((cell, idx) => {
          if (idx !== dayIndex) return normalizeDayCell(cell);
          const cellNorm = normalizeDayCell(cell);
          return { shifts: cellNorm.shifts.filter((_, i) => i !== slotIndex) };
        });
        return { ...row, days };
      });
      return recalculateInvoiceTotals({ ...prev, employees });
    });
  }, []);

  const removeEmployee = useCallback((rowId: string) => {
    setReportData((prev) => {
      if (!prev) return prev;
      return recalculateInvoiceTotals({
        ...prev,
        employees: prev.employees.filter((r) => r.id !== rowId),
      });
    });
  }, []);

  const handleAddEmployee = useCallback(() => {
    setReportData((prev) => (prev ? addEmptyEmployeeRow(prev) : prev));
  }, []);

  const handleResetEdits = useCallback(() => {
    if (originalReportData) {
      applyReportData(originalReportData, loadedRunId);
    }
  }, [originalReportData, loadedRunId, applyReportData]);

  const buildSavePayload = useCallback(
    (data: InvoiceReportData): InvoiceReportSavePayload | null => {
      if (!selectedScheduleId || !monthValue) return null;
      const final = recalculateInvoiceTotals(data);
      return {
        scheduleId: selectedScheduleId,
        scheduleName: selectedSchedule?.name ?? final.areaName,
        month: monthValue,
        locationId: debouncedSelectedLocationId ?? null,
        recordsProcessed: final.employees.length,
        grandTotalHours: final.grandTotalHours,
        parameters: {
          scheduleId: selectedScheduleId,
          scheduleName: selectedSchedule?.name ?? final.areaName,
          month: monthValue,
          locationId: debouncedSelectedLocationId ?? null,
        },
        snapshot: final,
      };
    },
    [selectedScheduleId, selectedSchedule, monthValue, debouncedSelectedLocationId]
  );

  const handleDownloadPdf = useCallback(async () => {
    if (!reportData) return;
    setDownloadBusy(true);
    try {
      const final = recalculateInvoiceTotals(reportData);
      const blob = createInvoiceReportPdfBlob(final);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeArea = reportData.areaName.replace(/[^a-zA-Z0-9]+/g, '_');
      link.href = url;
      link.download = `Invoice_Report_${safeArea}_${reportData.year}-${String(reportData.month).padStart(2, '0')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setDownloadBusy(false);
    }
  }, [reportData]);

  const handleSaveToHistory = useCallback(async () => {
    if (!reportData || !onSaveRun) return;
    const payload = buildSavePayload(reportData);
    if (!payload) {
      setError('Select a schedule and month before saving.');
      return;
    }
    setSaveBusy(true);
    setError(null);
    try {
      const saved = await onSaveRun(payload);
      applyReportData(payload.snapshot, saved.id);
      setSaveMessage('Report saved to history.');
    } catch {
      /* onSaveRun alerts */
    } finally {
      setSaveBusy(false);
    }
  }, [reportData, onSaveRun, buildSavePayload, applyReportData]);

  const loadHistoryRun = useCallback(
    async (item: ReportHistoryItem) => {
      setLoadBusyId(item.id);
      setError(null);
      try {
        let snapshot = item.invoiceReportSnapshot;
        if (!snapshot && item.serverInvoiceReportRunId != null) {
          const detail = await invoiceReportRunsApi.get(item.serverInvoiceReportRunId);
          snapshot = detail.snapshot;
          const month = detail.month;
          if (month) setMonthValue(month);
          if (detail.scheduleId) setSelectedScheduleId(detail.scheduleId);
        }
        const parsed = parseInvoiceReportSnapshot(snapshot);
        if (!parsed) throw new Error('Saved report data is invalid.');
        applyReportData(parsed, item.id);
        const p = item.parameters as { month?: string; scheduleId?: number };
        if (p.month) setMonthValue(p.month);
        if (p.scheduleId) setSelectedScheduleId(p.scheduleId);
      } catch (e) {
        console.error(e);
        setError('Could not load saved report.');
      } finally {
        setLoadBusyId(null);
      }
    },
    [applyReportData]
  );

  const downloadHistoryPdf = useCallback(async (item: ReportHistoryItem) => {
    setLoadBusyId(item.id);
    try {
      let snapshot = item.invoiceReportSnapshot;
      if (!snapshot && item.serverInvoiceReportRunId != null) {
        const detail = await invoiceReportRunsApi.get(item.serverInvoiceReportRunId);
        snapshot = detail.snapshot;
      }
      const parsed = parseInvoiceReportSnapshot(snapshot);
      if (!parsed) throw new Error('Invalid snapshot');
      const blob = createInvoiceReportPdfBlob(recalculateInvoiceTotals(parsed));
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const p = item.parameters as { scheduleName?: string; month?: string };
      const safeArea = (p.scheduleName ?? 'Invoice').replace(/[^a-zA-Z0-9]+/g, '_');
      link.href = url;
      link.download = `Invoice_Report_${safeArea}_${p.month ?? 'saved'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setError('Could not download saved report PDF.');
    } finally {
      setLoadBusyId(null);
    }
  }, []);

  const hasEdits = useMemo(() => {
    if (!reportData || !originalReportData) return false;
    return JSON.stringify(reportData) !== JSON.stringify(originalReportData);
  }, [reportData, originalReportData]);

  const formatHistoryLabel = (item: ReportHistoryItem) => {
    const p = item.parameters as { scheduleName?: string; month?: string; grandTotalHours?: number };
    return `${p.scheduleName ?? 'Schedule'} · ${p.month ?? ''} · ${Number(p.grandTotalHours ?? 0).toFixed(2)} hrs · ${item.recordsProcessed ?? 0} employees`;
  };

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-[100%]">
      <div>
        <h2 className="text-xl font-bold text-neutral-900">Invoice Report</h2>
        <p className="text-sm text-neutral-600 mt-1">
          Generate a monthly schedule invoice, edit if needed, save to history, and download the PDF anytime.
        </p>
      </div>

      <div className="bg-white rounded-lg border border-neutral-200 p-4 space-y-4 max-w-3xl relative z-10">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[200px] flex-1">
            <label className="block text-sm font-medium text-neutral-700 mb-1">Schedule</label>
            <select
              value={selectedScheduleId}
              onChange={(e) => setSelectedScheduleId(e.target.value ? Number(e.target.value) : '')}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm bg-white disabled:bg-neutral-50 disabled:text-neutral-500"
            >
              <option value="">
                {loadingOptions ? 'Loading schedules…' : 'Select schedule…'}
              </option>
              {schedules.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              <Calendar className="inline h-4 w-4 mr-1 align-text-bottom" />
              Month
            </label>
            <input
              type="month"
              value={monthValue}
              onChange={(e) => setMonthValue(e.target.value)}
              className="px-3 py-2 border border-neutral-300 rounded-md text-sm"
            />
          </div>

          <button
            type="button"
            onClick={() => void handleRunReport()}
            disabled={running || loadingOptions || !selectedScheduleId || !monthValue}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Run report
          </button>
        </div>

        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>
        )}
        {saveMessage && (
          <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
            {saveMessage}
          </div>
        )}
      </div>

      {reportData && (
        <div className="space-y-4">
          <div className="bg-white rounded-lg border border-neutral-200 p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-base font-semibold text-neutral-900">Report preview &amp; edit</h3>
              <div className="flex flex-wrap items-center gap-2">
                {hasEdits && (
                  <button
                    type="button"
                    onClick={handleResetEdits}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-neutral-300 rounded-md hover:bg-neutral-50"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Reset edits
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleAddEmployee}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-neutral-300 rounded-md hover:bg-neutral-50"
                >
                  <UserPlus className="h-4 w-4" />
                  Add employee
                </button>
                {onSaveRun && (
                  <button
                    type="button"
                    onClick={() => void handleSaveToHistory()}
                    disabled={saveBusy}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-blue-300 text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 disabled:opacity-50"
                  >
                    {saveBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Save to history
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleDownloadPdf}
                  disabled={downloadBusy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50"
                >
                  {downloadBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  Download PDF
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">Area</label>
                <input
                  type="text"
                  value={reportData.areaName}
                  onChange={(e) => updateMeta('areaName', e.target.value)}
                  className="w-full px-2 py-1.5 border border-neutral-300 rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">Month title</label>
                <input
                  type="text"
                  value={reportData.monthLabel}
                  onChange={(e) => updateMeta('monthLabel', e.target.value)}
                  className="w-full px-2 py-1.5 border border-neutral-300 rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">Company name</label>
                <input
                  type="text"
                  value={reportData.companyName}
                  onChange={(e) => updateMeta('companyName', e.target.value)}
                  className="w-full px-2 py-1.5 border border-neutral-300 rounded text-sm"
                />
              </div>
            </div>
            <p className="text-xs text-neutral-500">
              Period: {reportData.periodStart} to {reportData.periodEnd} · {reportData.employees.length} employee
              {reportData.employees.length === 1 ? '' : 's'} · {reportData.grandTotalHours.toFixed(2)} total hours
              {loadedRunId ? ' · Loaded from saved history' : ''}
            </p>
          </div>

          <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-max w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-neutral-100">
                    <th className="sticky left-0 z-20 bg-neutral-100 border border-neutral-200 px-2 py-2.5 text-left min-w-[140px] max-w-[180px] font-semibold">
                      Employee
                    </th>
                    {reportData.dayHeaders.map((dh, idx) => (
                      <th
                        key={idx}
                        className="border border-neutral-200 px-1 py-1.5 text-center min-w-[64px] font-normal leading-snug"
                      >
                        <div className="font-semibold">{dh.weekday}</div>
                        <div className="font-bold">{dh.day}</div>
                        <div className="text-neutral-500 text-xs">S</div>
                      </th>
                    ))}
                    <th className="border border-neutral-200 px-2 py-2 w-10" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {reportData.employees.length === 0 ? (
                    <tr>
                      <td
                        colSpan={reportData.daysInMonth + 2}
                        className="border border-neutral-200 px-4 py-8 text-center text-neutral-500"
                      >
                        No shifts found for this schedule and month. Add employees manually or choose a different period.
                      </td>
                    </tr>
                  ) : (
                    reportData.employees.map((row) => (
                      <EmployeeRowEditor
                        key={row.id}
                        row={row}
                        daysInMonth={reportData.daysInMonth}
                        onNameChange={updateEmployeeName}
                        onUpdateShiftSlot={updateShiftSlot}
                        onAddShiftSlot={addShiftSlot}
                        onRemoveShiftSlot={removeShiftSlot}
                        onRemove={removeEmployee}
                      />
                    ))
                  )}
                  <tr className="bg-neutral-50 font-bold">
                    <td className="sticky left-0 z-10 bg-neutral-50 border border-neutral-200 px-2 py-2 leading-snug align-top">
                      <div>Total</div>
                      {(() => {
                        const parts = formatInvoiceHoursThreeLine(reportData.grandTotalHours);
                        if (!parts) return null;
                        return (
                          <div className="mt-1 text-sm leading-tight">
                            <div>{parts.hoursLine}</div>
                            <div>{parts.minutesLine}</div>
                            <div>{parts.unitLine}</div>
                          </div>
                        );
                      })()}
                    </td>
                    {reportData.dailyTotals.map((hrs, idx) => {
                      const parts = formatInvoiceHoursThreeLine(hrs);
                      return (
                        <td key={idx} className="border border-neutral-200 px-1 py-1 text-center align-top">
                          {parts ? (
                            <div className="mx-0.5 my-0.5 px-0.5 py-1 border border-neutral-200 rounded bg-white text-sm leading-tight">
                              <div>{parts.hoursLine}</div>
                              <div>{parts.minutesLine}</div>
                              <div>{parts.unitLine}</div>
                            </div>
                          ) : (
                            ''
                          )}
                        </td>
                      );
                    })}
                    <td className="border border-neutral-200" />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-xs text-neutral-500">
            Tip: Enter shift times as 24-hour values without a colon (e.g. 1730). Use + in a day cell to add another
            shift on the same day.
          </p>
        </div>
      )}

      {sortedHistory.length > 0 && (
        <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-neutral-200 bg-neutral-50">
            <h3 className="text-sm font-semibold text-neutral-900">Saved reports</h3>
            <p className="text-xs text-neutral-500 mt-0.5">Open a saved report to edit again or download the PDF.</p>
          </div>
          <div className="divide-y divide-neutral-100 max-h-64 overflow-y-auto">
            {sortedHistory.map((item) => (
              <div key={item.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 hover:bg-neutral-50">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-neutral-900 truncate">{formatHistoryLabel(item)}</div>
                  <div className="text-xs text-neutral-500">
                    {formatCompanyDateTime(item.createdAt)} · {item.recordsProcessed ?? 0} employees
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    type="button"
                    disabled={loadBusyId === item.id}
                    onClick={() => void loadHistoryRun(item)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 disabled:opacity-50"
                  >
                    {loadBusyId === item.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <FolderOpen className="h-3 w-3" />
                    )}
                    Open
                  </button>
                  <button
                    type="button"
                    disabled={loadBusyId === item.id}
                    onClick={() => void downloadHistoryPdf(item)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded hover:bg-emerald-100 disabled:opacity-50"
                  >
                    <Download className="h-3 w-3" />
                    PDF
                  </button>
                  {onDeleteRun && (
                    <button
                      type="button"
                      onClick={() => onDeleteRun(item.id)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const EmployeeRowEditor: React.FC<{
  row: InvoiceEmployeeRow;
  daysInMonth: number;
  onNameChange: (rowId: string, name: string) => void;
  onUpdateShiftSlot: (
    rowId: string,
    dayIndex: number,
    slotIndex: number,
    field: keyof InvoiceShiftSlot,
    value: string
  ) => void;
  onAddShiftSlot: (rowId: string, dayIndex: number) => void;
  onRemoveShiftSlot: (rowId: string, dayIndex: number, slotIndex: number) => void;
  onRemove: (rowId: string) => void;
}> = ({
  row,
  daysInMonth,
  onNameChange,
  onUpdateShiftSlot,
  onAddShiftSlot,
  onRemoveShiftSlot,
  onRemove,
}) => {
  const [expandedDay, setExpandedDay] = useState<number | null>(null);

  return (
    <tr className="hover:bg-blue-50/40">
      <td className="sticky left-0 z-10 bg-white border border-neutral-200 px-2 py-1.5 align-top min-w-[140px] max-w-[180px]">
        <input
          type="text"
          value={row.employeeName}
          onChange={(e) => onNameChange(row.id, e.target.value)}
          className="w-full px-0.5 py-0.5 border border-transparent hover:border-neutral-300 focus:border-blue-400 rounded text-sm bg-transparent font-medium whitespace-nowrap overflow-hidden text-ellipsis"
          title={row.employeeName}
        />
        <div className="text-xs text-neutral-600 mt-0.5 pl-0.5">{row.totalHours.toFixed(2)} hrs</div>
      </td>
      {row.days.slice(0, daysInMonth).map((rawCell, dayIdx) => {
        const cell = normalizeDayCell(rawCell);
        const filledShifts = getDayShifts(cell);
        const editShifts =
          cell.shifts.length > 0 ? cell.shifts : [{ startTime: '', endTime: '' }];
        const isExpanded = expandedDay === dayIdx;
        const hasShift = filledShifts.length > 0;

        return (
          <td
            key={dayIdx}
            className={`border border-neutral-200 px-0.5 py-0.5 text-center align-top ${
              hasShift ? 'bg-blue-50/40' : ''
            }`}
          >
            {isExpanded ? (
              <div className="space-y-1 p-0.5 min-w-[72px]">
                {editShifts.map((slot, slotIdx) => (
                    <div key={slotIdx} className="relative border border-neutral-200 rounded bg-white p-0.5">
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="Start"
                        maxLength={4}
                        value={slot.startTime}
                        onChange={(e) =>
                          onUpdateShiftSlot(row.id, dayIdx, slotIdx, 'startTime', e.target.value)
                        }
                        className="w-full px-1 py-0.5 border border-neutral-200 rounded text-xs text-center"
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="End"
                        maxLength={4}
                        value={slot.endTime}
                        onChange={(e) =>
                          onUpdateShiftSlot(row.id, dayIdx, slotIdx, 'endTime', e.target.value)
                        }
                        className="w-full px-1 py-0.5 border border-neutral-200 rounded text-xs text-center mt-0.5"
                      />
                      {editShifts.length > 1 && (
                        <button
                          type="button"
                          onClick={() => onRemoveShiftSlot(row.id, dayIdx, slotIdx)}
                          className="absolute -top-1 -right-1 p-0.5 bg-white border border-red-200 rounded-full text-red-600 hover:bg-red-50"
                          title="Remove shift"
                        >
                          <Trash2 className="h-2.5 w-2.5" />
                        </button>
                      )}
                    </div>
                  )
                )}
                <div className="flex items-center justify-between gap-1 pt-0.5">
                  <button
                    type="button"
                    onClick={() => onAddShiftSlot(row.id, dayIdx)}
                    className="flex-1 inline-flex items-center justify-center gap-0.5 py-0.5 text-xs text-blue-700 border border-blue-200 rounded hover:bg-blue-50"
                    title="Add another shift"
                  >
                    <Plus className="h-3 w-3" />
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => setExpandedDay(null)}
                    className="px-1.5 py-0.5 text-xs text-neutral-600 border border-neutral-200 rounded hover:bg-neutral-50"
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col min-h-[42px]">
                <button
                  type="button"
                  onClick={() => setExpandedDay(dayIdx)}
                  className="flex-1 w-full hover:bg-blue-100 rounded text-left"
                  title="Click to edit shifts"
                >
                  {hasShift ? (
                    <div className="mx-0.5 mt-0.5 px-0.5 py-1 border border-neutral-200 rounded bg-white text-sm leading-snug text-center">
                      {filledShifts.map((slot, i) => (
                        <div key={i} className={i > 0 ? 'mt-1 pt-1 border-t border-neutral-100' : ''}>
                          <div className="font-medium">{slot.startTime}</div>
                          <div className="font-medium">{slot.endTime}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="block py-2" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onAddShiftSlot(row.id, dayIdx);
                    setExpandedDay(dayIdx);
                  }}
                  className="mx-auto mb-0.5 p-0.5 text-blue-600 hover:bg-blue-100 rounded"
                  title="Add shift"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </td>
        );
      })}
      <td className="border border-neutral-200 px-1 py-1 text-center">
        <button
          type="button"
          onClick={() => onRemove(row.id)}
          className="p-1 text-red-600 hover:bg-red-50 rounded"
          title="Remove employee"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
};

export default InvoiceReport;
